import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, Settings } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface LocationData {
  location: string;
  value: number;
  count: number;
  color: string;
}

interface AssetsByLocationCardProps {
  readonly data: LocationData[];
  readonly className?: string;
}

export function AssetsByLocationCard({ data = [], className }: AssetsByLocationCardProps): React.ReactElement {
  const navigate = useNavigate();
  const [metric, setMetric] = useState<'count' | 'value'>('count');

  const hasData = Array.isArray(data) && data.length > 0;

  // Calculate max value for scaling based on selected metric
  const maxValue = hasData ? Math.max(...data.map(d => metric === 'count' ? d.count : d.value)) : 0;
  const totalValue = data.reduce((sum, d) => sum + (metric === 'count' ? d.count : d.value), 0);
  const topLocation = hasData ? data.reduce((max, item) => {
    const itemMetric = metric === 'count' ? item.count : item.value;
    const maxMetric = metric === 'count' ? max.count : max.value;
    return itemMetric > maxMetric ? item : max;
  }, data[0]) : undefined;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Handle bar click to navigate to filtered assets
  const handleLocationClick = (location: string) => {
    navigate(`/assets?location=${encodeURIComponent(location)}`);
  };

  if (!hasData) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col', className)}>
        <CardHeader className="relative">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Assets by Location</CardTitle>
              <CardDescription>Asset value distribution across locations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="text-sm text-muted-foreground">No location data available.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col', className)}>

      
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Assets by Location</CardTitle>
            <CardDescription>
              {metric === 'count' ? 'Asset count distribution across locations' : 'Asset value distribution across locations'}
            </CardDescription>
          </div>
          <div className="flex gap-1 items-center">
            <Select value={metric} onValueChange={(value: 'count' | 'value') => setMetric(value)}>
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="value">Value</SelectItem>
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    This chart shows the total asset {metric === 'count' ? 'count' : 'value'} distributed across different office locations. 
                    Each bar represents the cumulative {metric === 'count' ? 'number' : 'value'} of all assets at that location.
                    <br /><br />
                    <strong>Click on any bar</strong> to view assets for that specific location.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-6">
          {/* Total Value Display */}
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold">
              {metric === 'count' 
                ? totalValue 
                : formatCurrency(totalValue)
              }
            </div>
            <div className="text-sm text-muted-foreground">
              {metric === 'count' ? 'Total Assets' : 'Total Asset Value'}
            </div>
          </div>

          {/* Bar Chart */}
          <div className="space-y-4">
            {data.map((item) => {
              const currentValue = metric === 'count' ? item.count : item.value;
              const percentage = maxValue > 0 ? (currentValue / maxValue) * 100 : 0;
              
              return (
                <button 
                  type="button"
                  key={`location-${item.location}`} 
                  className="space-y-2 cursor-pointer group w-full text-left"
                  onClick={() => handleLocationClick(item.location)}
                  aria-label={`View assets in ${item.location}`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full transition-transform group-hover:scale-110" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-medium group-hover:text-foreground transition-colors">{item.location}</span>
                    </div>
                    <span className="font-semibold group-hover:text-foreground transition-colors">
                      {metric === 'count' 
                        ? `${item.count} assets` 
                        : formatCurrency(item.value)
                      }
                    </span>
                  </div>
                  <div className="relative h-8 w-full overflow-hidden rounded-md bg-muted group-hover:bg-muted/80 transition-colors">
                    <div
                      className="h-full transition-all duration-500 ease-out group-hover:brightness-110"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Highest Value</p>
              <p className="text-sm font-semibold">
                {topLocation?.location ?? '—'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Locations</p>
              <p className="text-sm font-semibold">{data.length} offices</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
